// WorkerDashboard.jsx - Mobile-first layout za terenske radnike
// Redizajniran sa HA_MINIMAL_FIELD_REPORT_FORM spec:
//   - 3-step workflow: ON_SITE → IN_PROGRESS → READY_FOR_QA
//   - Field report sa GPS, work_summary, deviation, declaration
//   - 5 photo types (4 required + 1 optional)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './WorkerDashboard.css';
import ApiService from '../services/api';

const REQUIRED_PHOTOS = [
  { type: 'apl_open', label: 'Otvoreni APL (splajcsanje)', icon: '📍' },
  { type: 'ont', label: 'Montiran ONT', icon: '📦' },
  { type: 'apl_closed', label: 'Zatvoreni APL', icon: '🔒' },
  { type: 'test_result', label: 'Test rezultat (dBm)', icon: '📊' },
  { type: 'optional', label: 'Opciono (zaptivanje)', icon: '🔧' }
];

const REQUIRED_TYPES = ['apl_open', 'ont', 'apl_closed', 'test_result'];

const WorkerDashboard = ({ user }) => {
  const [terminals, setTerminals] = useState([]);
  const [currentTerminal, setCurrentTerminal] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userName, setUserName] = useState(user?.name || user?.email || '');

  // Field report form state
  const [workSummary, setWorkSummary] = useState('');
  const [hasDeviation, setHasDeviation] = useState(false);
  const [deviationNote, setDeviationNote] = useState('');
  const [declarationComplete, setDeclarationComplete] = useState(false);
  const [declarationByRules, setDeclarationByRules] = useState(false);
  const [signedBy, setSignedBy] = useState('');

  const geoRef = useRef(null);

  // Preuzmi dodeljene terminale
  useEffect(() => {
    fetchTerminals();
    startGPSTracking();
    loadOfflineQueue().then(setOfflineQueue);
  }, []);

  // Učitaj field report i fotografije kada se otvori terminal
  useEffect(() => {
    if (currentTerminal) {
      loadFieldReport(currentTerminal.id);
      loadPhotos(currentTerminal.id);
    }
  }, [currentTerminal]);

  const fetchTerminals = async () => {
    try {
      const data = await ApiService.listTerminals({ assigned_to: 'current_user_id' });
      setTerminals(data);
    } catch (err) {
      console.error('Offline ili greška pri preuzimanju:', err);
      loadFromIndexedDB();
    }
  };

  const startGPSTracking = () => {
    if (navigator.geolocation) {
      navigator.geolocation.watchPosition((position) => {
        geoRef.current = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: Date.now()
        };
      }, (err) => {
        console.warn('GPS greška:', err.message);
      }, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 30000
      });
    }
  };

  const loadFieldReport = async (terminalId) => {
    setReportLoading(true);
    try {
      const data = await ApiService.getFieldReportByTerminal(terminalId);
      setReport(data);
      // Popuni formu iz postojećeg izveštaja
      setWorkSummary(data.work_summary || '');
      setHasDeviation(data.has_deviation || false);
      setDeviationNote(data.deviation_note || '');
      setDeclarationComplete(data.declaration_complete || false);
      setDeclarationByRules(data.declaration_by_rules || false);
      setSignedBy(data.signed_by || '');
    } catch (err) {
      // Nema izveštaja - nova sesija
      setReport(null);
      resetForm();
    } finally {
      setReportLoading(false);
    }
  };

  const loadPhotos = async (terminalId) => {
    try {
      const data = await ApiService.listTerminalPhotos(terminalId);
      setPhotos(data || []);
      // Postavi uploadProgress na osnovu postojećih fotografija
      const progress = {};
      (data || []).forEach(p => {
        progress[p.photo_type] = 'done';
      });
      setUploadProgress(progress);
    } catch (err) {
      console.error('Greška pri učitavanju fotografija:', err);
    }
  };

  const resetForm = () => {
    setWorkSummary('');
    setHasDeviation(false);
    setDeviationNote('');
    setDeclarationComplete(false);
    setDeclarationByRules(false);
    setSignedBy('');
  };

  const handleStartTerminal = async (terminal) => {
    // GPS check (warning ako > 100m)
    if (geoRef.current && terminal.location_lat && terminal.location_lng) {
      const distance = calculateDistance(
        geoRef.current,
        { lat: terminal.location_lat, lng: terminal.location_lng }
      );
      if (distance > 100) {
        alert(`⚠️ Udaljen si ${Math.round(distance)}m od lokacije! Nastavi?`);
      }
    }

    try {
      await ApiService.startTerminal(terminal.id);
      // Kreiraj field report sa ON_SITE statusom
      await ApiService.createOrUpdateFieldReport({
        terminal_id: terminal.id,
        status: 'ON_SITE',
        latitude: geoRef.current?.lat || 0,
        longitude: geoRef.current?.lng || 0,
        geo_accuracy: geoRef.current?.accuracy || 0,
        signed_by: userName,
      });
      setCurrentTerminal(terminal);
    } catch (err) {
      console.error('Greška pri start-u:', err);
    }
  };

  const handlePhotoUpload = async (photoType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('terminal_id', currentTerminal.id);
      formData.append('photo_type', photoType);
      formData.append('latitude', geoRef.current?.lat || 0);
      formData.append('longitude', geoRef.current?.lng || 0);
      formData.append('geo_accuracy', geoRef.current?.accuracy || 0);
      formData.append('file', file);

      try {
        setUploadProgress(prev => ({ ...prev, [photoType]: 'uploading' }));
        await ApiService.uploadPhoto(formData);
        setUploadProgress(prev => ({ ...prev, [photoType]: 'done' }));
        // Osveži listu fotografija
        loadPhotos(currentTerminal.id);
      } catch (err) {
        // Offline - čuvaj u IndexedDB queue
        queuePhotoOffline(currentTerminal.id, photoType, file);
        setUploadProgress(prev => ({ ...prev, [photoType]: 'queued' }));
      }
    };

    input.click();
  };

  const handleStatusChange = async (newStatus) => {
    if (!currentTerminal) return;
    setSubmitting(true);

    try {
      await ApiService.createOrUpdateFieldReport({
        terminal_id: currentTerminal.id,
        status: newStatus,
        latitude: geoRef.current?.lat || 0,
        longitude: geoRef.current?.lng || 0,
        geo_accuracy: geoRef.current?.accuracy || 0,
        work_summary: workSummary,
        has_deviation: hasDeviation,
        deviation_note: hasDeviation ? deviationNote : '',
        declaration_complete: declarationComplete,
        declaration_by_rules: declarationByRules,
        signed_by: signedBy || userName,
      });

      // Osveži report
      await loadFieldReport(currentTerminal.id);

      // Ako je READY_FOR_QA, vrati se na listu
      if (newStatus === 'READY_FOR_QA') {
        setCurrentTerminal(null);
        fetchTerminals();
      }
    } catch (err) {
      console.error('Greška pri ažuriranju statusa:', err);
      alert('Greška pri čuvanju izveštaja. Pokušaj ponovo.');
    } finally {
      setSubmitting(false);
    }
  };

  const queuePhotoOffline = async (terminalId, photoType, file) => {
    const db = await openIndexedDB();
    const tx = db.transaction(['offlineQueue'], 'readwrite');
    const store = tx.objectStore('offlineQueue');
    const blob = await file.arrayBuffer();
    store.add({
      terminal_id: terminalId,
      photo_type: photoType,
      blob: blob,
      timestamp: Date.now(),
      location: geoRef.current
    });
  };

  const syncOfflineQueue = async () => {
    const offlineQueue = await getAllOfflinePhotos();
    for (const item of offlineQueue) {
      const formData = new FormData();
      formData.append('terminal_id', item.terminal_id);
      formData.append('photo_type', item.photo_type);
      formData.append('latitude', item.location?.lat || 0);
      formData.append('longitude', item.location?.lng || 0);
      formData.append('file', new Blob([item.blob]));
      try {
        await ApiService.uploadPhoto(formData);
        await deleteOfflinePhoto(item.id);
      } catch (err) {
        console.error('Sync još nije moguć:', err);
        break;
      }
    }
    const remaining = await loadOfflineQueue();
    setOfflineQueue(remaining);
  };

  const allRequiredPhotosDone = () => {
    return REQUIRED_TYPES.every(type => uploadProgress[type] === 'done');
  };

  const getCurrentStatus = () => {
    if (!report) return 'ON_SITE';
    return report.status;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'ON_SITE': 'Na lokaciji',
      'IN_PROGRESS': 'U radu',
      'READY_FOR_QA': 'Spremno za pregled',
      // Upisuje QA odobrenje (backend: qa_handlers.go).
      'COMPLETED': 'Završeno'
    };
    return labels[status] || status;
  };

  // ==================== RENDER: LISTA TERMINALA ====================
  if (!currentTerminal) {
    return (
      <div className="worker-dashboard">
        <div className="terminal-list">
          <div className="list-header">
            <h1>📍 Moji Dnevni Nalozi</h1>
            <span className="gps-indicator" title={geoRef.current ? 'GPS aktivan' : 'GPS nije dostupan'}>
              {geoRef.current ? '🟢' : '🔴'} GPS
            </span>
          </div>

          <div className="filter-bar">
            <select onChange={(e) => {/* filtriraj po prioritetu */}}>
              <option value="">Svi prioriteti</option>
              <option value="high">🔴 High Priority</option>
              <option value="medium">🟡 Medium</option>
              <option value="low">🟢 Low</option>
            </select>
          </div>

          <div className="terminals">
            {terminals.length === 0 && (
              <div className="empty-state">
                <p>📭 Nema dodeljenih naloga za danas.</p>
              </div>
            )}
            {terminals.map(terminal => (
              <div key={terminal.id} className="terminal-card">
                <div className="terminal-header">
                  <h3>{terminal.street} {terminal.house_number}</h3>
                  <span className={`status-badge ${terminal.status}`}>
                    {terminal.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="terminal-info">
                  <p>🏙️ {terminal.city}</p>
                  <p>📡 Kabinet: {terminal.nvt_cabinet_id}</p>
                  <p>🖼️ {terminal.photo_count || 0}/5 slika</p>
                </div>
                {terminal.status === 'assigned' && (
                  <button
                    className="btn-start"
                    onClick={() => handleStartTerminal(terminal)}
                  >
                    ▶ POČNI RAD
                  </button>
                )}
                {terminal.status === 'in_progress' && (
                  <button
                    className="btn-resume"
                    onClick={() => setCurrentTerminal(terminal)}
                  >
                    ↩ Nastavi rad
                  </button>
                )}
                {terminal.status === 'pending_qa' && (
                  <div className="status-msg pending-qa-msg">
                    ⏳ Čeka QA pregled
                  </div>
                )}
                {terminal.status === 'completed' && (
                  <div className="status-msg completed-msg">
                    ✅ Završeno
                  </div>
                )}
              </div>
            ))}
          </div>

          {offlineQueue.length > 0 && (
            <div className="offline-banner">
              <p>📤 {offlineQueue.length} fotografija čeka slanje (Offline mode)</p>
              <button onClick={syncOfflineQueue} disabled={!navigator.onLine}>
                Sinhronizuj sada
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== RENDER: RAD NA TERMINALU ====================
  const currentStatus = getCurrentStatus();
  const canProgress = currentStatus === 'ON_SITE' || currentStatus === 'IN_PROGRESS';
  const canComplete = currentStatus === 'IN_PROGRESS' && allRequiredPhotosDone();

  return (
    <div className="worker-dashboard">
      <div className="terminal-work">
        {/* Header */}
        <div className="work-header">
          <button onClick={() => setCurrentTerminal(null)} className="btn-back">
            ← Nazad
          </button>
          <div className="work-title">
            <h2>{currentTerminal.street} {currentTerminal.house_number}</h2>
            <span className={`status-badge ${currentStatus}`}>
              {getStatusLabel(currentStatus)}
            </span>
          </div>
        </div>

        {/* GPS Info */}
        <div className="gps-info">
          <span>📍 {geoRef.current ? `${geoRef.current.lat.toFixed(5)}, ${geoRef.current.lng.toFixed(5)}` : 'Čekam GPS...'}</span>
          <span className="accuracy">±{Math.round(geoRef.current?.accuracy || 0)}m</span>
        </div>

        {/* Stepper */}
        <div className="stepper">
          <div className={`step ${currentStatus === 'ON_SITE' ? 'active' : 'done'}`}>
            <div className="step-circle">{currentStatus === 'ON_SITE' ? '1' : '✓'}</div>
            <div className="step-label">Na lokaciji</div>
          </div>
          <div className="step-line" />
          <div className={`step ${currentStatus === 'IN_PROGRESS' ? 'active' : currentStatus === 'READY_FOR_QA' ? 'done' : ''}`}>
            <div className="step-circle">{currentStatus === 'READY_FOR_QA' ? '✓' : '2'}</div>
            <div className="step-label">U radu</div>
          </div>
          <div className="step-line" />
          <div className={`step ${currentStatus === 'READY_FOR_QA' ? 'active' : ''}`}>
            <div className="step-circle">3</div>
            <div className="step-label">Spremno</div>
          </div>
        </div>

        {/* ===== FOTOGRAFIJE ===== */}
        <div className="section">
          <h3 className="section-title">📸 Fotografije</h3>
          <div className="photo-grid">
            {REQUIRED_PHOTOS.map(photo => (
              <div key={photo.type} className="photo-item">
                <button
                  className={`photo-button ${uploadProgress[photo.type] || ''}`}
                  onClick={() => handlePhotoUpload(photo.type)}
                  disabled={uploadProgress[photo.type] === 'done'}
                >
                  <span className="photo-icon">{photo.icon}</span>
                  <span className="photo-label">{photo.label}</span>
                  <span className="photo-status">
                    {uploadProgress[photo.type] === 'uploading' && '⟳'}
                    {uploadProgress[photo.type] === 'done' && '✓'}
                    {uploadProgress[photo.type] === 'queued' && '⏳'}
                    {!uploadProgress[photo.type] && '📷'}
                  </span>
                </button>
              </div>
            ))}
          </div>
          <div className="photo-progress">
            {REQUIRED_TYPES.filter(t => uploadProgress[t] === 'done').length}/{REQUIRED_TYPES.length} obaveznih
          </div>
        </div>

        {/* ===== RADNI IZVEŠTAJ ===== */}
        <div className="section">
          <h3 className="section-title">📋 Radni izveštaj</h3>

          {/* Work Summary */}
          <div className="form-group">
            <label>Opis obavljenog posla</label>
            <textarea
              className="form-textarea"
              rows="3"
              maxLength="300"
              placeholder="Ukratko opiši šta je urađeno na terenu..."
              value={workSummary}
              onChange={(e) => setWorkSummary(e.target.value)}
              disabled={currentStatus === 'READY_FOR_QA'}
            />
            <span className="char-count">{workSummary.length}/300</span>
          </div>

          {/* Deviation */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={hasDeviation}
                onChange={(e) => setHasDeviation(e.target.checked)}
                disabled={currentStatus === 'READY_FOR_QA'}
              />
              <span>⚠️ Odstupanje od plana</span>
            </label>
          </div>

          {hasDeviation && (
            <div className="form-group">
              <label>Opis odstupanja</label>
              <textarea
                className="form-textarea"
                rows="2"
                placeholder="Detaljno opiši odstupanje..."
                value={deviationNote}
                onChange={(e) => setDeviationNote(e.target.value)}
                disabled={currentStatus === 'READY_FOR_QA'}
              />
            </div>
          )}

          {/* Declaration */}
          <div className="form-group declaration-group">
            <h4>📝 Izjava izvođača radova</h4>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={declarationComplete}
                onChange={(e) => setDeclarationComplete(e.target.checked)}
                disabled={currentStatus === 'READY_FOR_QA'}
              />
              <span>Radovi su izvedeni u potpunosti</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={declarationByRules}
                onChange={(e) => setDeclarationByRules(e.target.checked)}
                disabled={currentStatus === 'READY_FOR_QA'}
              />
              <span>Radovi su izvedeni po pravilima struke</span>
            </label>
            <div className="form-group">
              <label>Ime i prezime (potpis)</label>
              <input
                type="text"
                className="form-input"
                placeholder="Unesi svoje ime i prezime"
                value={signedBy}
                onChange={(e) => setSignedBy(e.target.value)}
                disabled={currentStatus === 'READY_FOR_QA'}
              />
            </div>
          </div>
        </div>

        {/* ===== AKCIJE ===== */}
        <div className="actions-section">
          {currentStatus === 'ON_SITE' && (
            <button
              className="btn-action btn-progress"
              onClick={() => handleStatusChange('IN_PROGRESS')}
              disabled={submitting}
            >
              {submitting ? '⏳ Čuvanje...' : '▶ Započni rad'}
            </button>
          )}

          {currentStatus === 'IN_PROGRESS' && (
            <>
              <button
                className="btn-action btn-save"
                onClick={() => handleStatusChange('IN_PROGRESS')}
                disabled={submitting}
              >
                {submitting ? '⏳ Čuvanje...' : '💾 Sačuvaj izveštaj'}
              </button>
              <button
                className="btn-action btn-complete"
                onClick={() => handleStatusChange('READY_FOR_QA')}
                disabled={!canComplete || submitting}
                title={!allRequiredPhotosDone() ? 'Nedostaju obavezne fotografije' : ''}
              >
                {submitting ? '⏳ Slanje...' : '✅ Završi nalog - Pošalji na pregled'}
              </button>
              {!allRequiredPhotosDone() && (
                <p className="validation-hint">
                  📸 Potrebno je uploadovati sve 4 obavezne fotografije pre završetka
                </p>
              )}
            </>
          )}

          {currentStatus === 'READY_FOR_QA' && (
            <div className="status-msg ready-msg">
              ✅ Nalog je poslat na QA pregled. Čekaj odobrenje rukovodioca.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerDashboard;

// ============ Utility Functions ============

function calculateDistance(gps1, gps2) {
  if (!gps1 || !gps2) return 0;
  const R = 6371;
  const dLat = ((gps2.lat - gps1.lat) * Math.PI) / 180;
  const dLng = ((gps2.lng - gps1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((gps1.lat * Math.PI) / 180) * Math.cos((gps2.lat * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 1000);
}

async function openIndexedDB() {
  return new Promise((resolve) => {
    const request = indexedDB.open('wtermins', 1);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('offlineQueue')) {
        db.createObjectStore('offlineQueue', { keyPath: 'id', autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

async function getAllOfflinePhotos() {
  const db = await openIndexedDB();
  return new Promise((resolve) => {
    const tx = db.transaction(['offlineQueue']);
    const store = tx.objectStore('offlineQueue');
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
  });
}

async function deleteOfflinePhoto(id) {
  const db = await openIndexedDB();
  const tx = db.transaction(['offlineQueue'], 'readwrite');
  const store = tx.objectStore('offlineQueue');
  store.delete(id);
}

async function loadFromIndexedDB() {
  const db = await openIndexedDB();
  const tx = db.transaction(['offlineQueue']);
  const store = tx.objectStore('offlineQueue');
  const request = store.getAll();
  request.onsuccess = () => {
    // Prikazati sa 'Offline' indikatorom
  };
}

async function loadOfflineQueue() {
  const queue = await getAllOfflinePhotos();
  return queue;
}